import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  OktaAuth,
  OktaAuthOptions,
  StorageManagerOptions,
  StorageType,
} from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import {
  SiteConfig,
  useBuildVars,
  useInX4AFrame,
  useSiteConfig,
} from '../../config';
import { AuthConfig, authConfigContext } from './auth-context';
import { OktaHelper } from './OktaHelper';
import { XvAppId } from '@im/ui-lib/mf-utils';

// No Op; The LoginView redirects to the original path!
const restoreOriginalUri = async () => undefined;

type Props = {
  children: ReactNode;
};

export function AuthProvider(props: Props) {
  const { children } = props;
  const siteConfig = useSiteConfig();
  const { appId = 'cep' } = useBuildVars();
  const { isInX4AFrame } = useInX4AFrame();
  const [authConfig, setAuthConfig] = useState<AuthConfig | undefined>(
    undefined
  );

  useEffect(() => {
    if (siteConfig) {
      setAuthConfig(createAuthConfig(siteConfig, isInX4AFrame, appId));
    }
  }, [siteConfig, isInX4AFrame, appId]);

  const oktaAuth = useMemo(() => {
    if (authConfig) {
      const options: OktaAuthOptions = authConfig;
      return new OktaAuth(options);
    }
  }, [authConfig]);

  if (!authConfig || !oktaAuth) {
    return null;
  }

  const { Provider } = authConfigContext;
  return (
    <Provider value={authConfig}>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <OktaHelper>{children}</OktaHelper>
      </Security>
    </Provider>
  );
}

function createAuthConfig(
  siteConfig: SiteConfig,
  isInFrame: boolean,
  appId: XvAppId
): AuthConfig {
  const storageType: StorageType = isInFrame
    ? 'sessionStorage'
    : 'localStorage';
  const storageManager: StorageManagerOptions = {
    cache: {
      storageType,
      storageTypes: ['localStorage', 'sessionStorage', 'cookie'],
    },
    'shared-transaction': {
      storageType,
      storageTypes: ['localStorage', 'sessionStorage', 'cookie'],
    },
    token: {
      storageType,
      storageTypes: ['localStorage', 'sessionStorage', 'cookie'],
    },
    transaction: {
      storageType,
      storageTypes: ['localStorage', 'sessionStorage', 'cookie'],
    },
  };

  return {
    clientId: siteConfig.clientId,
    disableHttpsCheck: false,
    ignoreLifetime: true,
    interactionCodeFlow: siteConfig.interactionCodeFlow,
    issuer: siteConfig.authServer,
    maxClockSkew: 21780, //6 hours 5 minutes
    pkce: true,
    redirectUri: `${window.location.origin}/${appId}/app/login/callback`,
    scopes: ['openid', 'profile', 'email', 'offline_access'],
    services: {
      autoRemove: false,
      autoRenew: false,
      syncStorage: true,
    },
    storageManager,
    tokenManager: {
      autoRemove: false,
      autoRenew: false,
      expireEarlySeconds: 240, //doesn't work
    },
  };
}
